/* istanbul ignore file */

import axios from "axios";
import get from "lodash/get";
import nanoid from "nanoid";

import { store } from "../store";
import { setAccessToken, setExpired } from "../store/actions/appConfig";
import { getRefreshToken } from "../store/selectors/appConfig";

import { NotificationsManager } from "../components/Notification";

import { encrypt, decrypt } from "./crypto";
import { log } from "../utils/loggger";
import { formatJsonData } from "./formatJsonData";
import { IGNORE_ERROR_CODES, HANDLED_ERROR_CODES, DEVICE_ID } from "../constants";
import {
  ErrorOccurredWhilePerforming,
  ReCaptchaError,
  FieldsValidationError
} from "./serverErrors";
import { endpoints } from "../constants/config";
import {
  PreAuthInstance as preAuthApiClient,
  PostAuthInstance as postAuthApiClient
} from "./DehAxiosConfig";

const SYM_KEY_HEADER = "x-sym-key";
const REQUEST_ID_HEADER = "x-request-id";
const ENCRYPT_METHODS = ["post", "put"];
const DEVICE_ID_HEADER = "device-id";
const ENCRYPTION_ENABLE = process.env.REACT_APP_ENCRYPTION_ENABLE || "N";
const rsaPublicKey = process.env.REACT_APP_RSA_PUBLIC_KEY;

const encryptionEnabled = ENCRYPTION_ENABLE === "Y";

export const uploadClient = axios.create({
  baseURL: process.env.REACT_APP_UPLOAD_PATH
});

const prepareHeaders = (config, apiClient) => {
  const headers = { ...config.headers };
  if (apiClient === preAuthApiClient) {
    delete headers["refresh-token"];
  }
  return headers;
};

[preAuthApiClient, postAuthApiClient].forEach(apiClient => {
  apiClient.interceptors.request.use(config => {
    const headers = prepareHeaders(config, apiClient);
    return {
      ...config,
      headers: {
        ...headers,
        "Cache-Control": "no-cache, no-store",
        Pragma: "no-cache",
        [REQUEST_ID_HEADER]: nanoid(),
        [DEVICE_ID_HEADER]: headers["device-id"] || DEVICE_ID
      }
    };
  });

  apiClient.interceptors.request.use(config => {
    // Check if payload is already encrypted
    const isAlreadyEncrypted =
      config.symKey || // symKey exists from previous encryption
      (typeof config.data === "string" && config.headers && config.headers[SYM_KEY_HEADER]); // data is string and has sym key header

    if (
      encryptionEnabled &&
      rsaPublicKey &&
      ENCRYPT_METHODS.includes(config.method.toLowerCase()) &&
      !config.url.includes("/oauth2/token") &&
      !isAlreadyEncrypted
    ) {
      const headers = prepareHeaders(config, apiClient);

      const [encryptedPayload, encryptedSymKey, symKey] = encrypt(
        rsaPublicKey,
        JSON.stringify(config.data)
      );

      return {
        ...config,
        headers: {
          ...headers,
          "Cache-Control": "no-cache, no-store",
          "Content-Type": "application/json",
          [SYM_KEY_HEADER]: encryptedSymKey
        },
        data: encryptedPayload,
        symKey
      };
    }

    return config;
  });
});

[preAuthApiClient, postAuthApiClient, uploadClient].forEach(instance => {
  instance.interceptors.response.use(response => {
    const state = store.getState();
    const accessToken = response.headers.accesstoken || response.headers.AccessToken;
    let refreshToken = getRefreshToken(state);
    if (instance === postAuthApiClient) {
      refreshToken = response.headers["refresh-token"] || "";
    }
    if (accessToken) {
      store.dispatch(setAccessToken(accessToken, refreshToken));
    }

    return response;
  });
});

[preAuthApiClient, postAuthApiClient].forEach(apiClient => {
  apiClient.interceptors.response.use(
    response => {
      const { symKey } = response.config;

      if (symKey && response.data && typeof response.data === "string") {
        let payload;

        try {
          payload = decrypt(symKey, response.data).data;
        } catch (e) {
          log({ e, symKey, data: response.data });
        }

        try {
          if (payload) {
            return {
              ...response,
              data: JSON.parse(payload)
            };
          }
        } catch (e) {
          log({ e, payload });
        }
      }

      return response;
    },
    error => {
      const {
        data,
        status,
        config: { symKey, url }
      } = error.response;

      let jsonData = data;

      if (symKey && data && typeof data === "string") {
        let payload;

        try {
          payload = decrypt(symKey, data).data;
        } catch (e) {
          log({ e, symKey, data });
        }

        try {
          if (payload) {
            jsonData = JSON.parse(payload);
          }
        } catch (e) {
          log({ e, payload });
        }
      }

      let notificationOptions = {};
      let serverError = null;
      const ignoreNotificationUrls = [endpoints.createInviteUri];

      if (jsonData) {
        const { errors, errorType } = jsonData;
        if (errorType === "ReCaptchaError") {
          serverError = new ReCaptchaError(jsonData);
          notificationOptions = { title: "ReCaptchaError", message: errors };
        } else if (errorType === "FieldsValidation") {
          serverError = new FieldsValidationError(jsonData);
          notificationOptions = {
            title: "Validation Error On Server",
            message: get(jsonData, "errors[0].message", "Validation Error")
          };
        } else if (HANDLED_ERROR_CODES.includes(get(errors, "[0].errorCode"))) {
          serverError = new ErrorOccurredWhilePerforming(jsonData);
          notificationOptions = null;
        } else if (ignoreNotificationUrls.includes(url)) {
          // ro-assist-brd3-1
          notificationOptions = null;
        } else {
          log(jsonData);
          try {
            if (jsonData.status) {
              if (IGNORE_ERROR_CODES.includes(get(errors, "[0].errorCode"))) {
                notificationOptions = null;
              } else {
                const errorMessages = errors.map(({ message }) => message);
                const debugNotificationOptions = formatJsonData(jsonData);

                notificationOptions = {
                  message: errorMessages.join(", "),
                  ...debugNotificationOptions
                };
              }
            }
          } catch (e) {
            log(e);
          }
        }
      } else if (status === 401) {
        store.dispatch(setExpired(true));
        notificationOptions = null;
      }

      if (notificationOptions && NotificationsManager.add) {
        NotificationsManager.add(notificationOptions);
      }

      return Promise.reject(serverError || error);
    }
  );
});

const apiClient = {
  preAuth: preAuthApiClient,
  postAuth: postAuthApiClient
};

export default apiClient;
